import { useMemo } from 'react'
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, format,
} from 'date-fns'
import { cn, getEventColor } from '../../../lib/utils'

const DAY_LABELS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const DAY_LABELS_LONG  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const MAX_BARS = 3

export const MonthGrid = ({ month, events, selectedDay, onDaySelect }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month))
    const end   = endOfWeek(endOfMonth(month))
    return eachDayOfInterval({ start, end })
  }, [month])

  const eventsByDay = useMemo(() => {
    const map = {}
    for (const event of events) {
      const key = (event.start_date ?? event.start_time)?.slice(0, 10)
      if (!key) continue
      if (!map[key]) map[key] = []
      map[key].push(event)
    }
    return map
  }, [events])

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-border">
        {DAY_LABELS_LONG.map((d, i) => (
          <div key={d} className="text-center text-[11px] font-semibold text-text-muted py-2">
            <span className="sm:hidden">{DAY_LABELS_SHORT[i]}</span>
            <span className="hidden sm:inline">{d}</span>
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 flex-1 divide-x divide-y divide-border">
        {days.map((day) => {
          const key     = format(day, 'yyyy-MM-dd')
          const dayEvts = eventsByDay[key] ?? []
          const inMonth = isSameMonth(day, month)
          const active  = selectedDay && isSameDay(day, selectedDay)
          const today   = isToday(day)
          const shown   = dayEvts.slice(0, MAX_BARS)
          const overflow = dayEvts.length - MAX_BARS

          return (
            <button
              key={key}
              onClick={() => onDaySelect(day)}
              className={cn(
                'relative flex flex-col items-stretch pt-1 pb-1 min-h-[80px] transition-colors overflow-hidden gap-px',
                inMonth ? 'bg-surface' : 'bg-background',
                active && 'bg-primary-light',
                !active && inMonth && 'hover:bg-background',
              )}
            >
              <span
                className={cn(
                  'text-[13px] font-medium w-7 h-7 flex items-center justify-center rounded-full self-center mb-0.5 flex-shrink-0',
                  !inMonth && 'text-text-muted',
                  inMonth && !today && 'text-text-primary',
                  today && 'bg-primary text-white',
                )}
              >
                {format(day, 'd')}
              </span>

              {shown.map((e) => (
                <span
                  key={e.id}
                  className="mx-0.5 px-1 text-[10px] font-medium text-white truncate rounded leading-[15px] flex-shrink-0"
                  style={{ backgroundColor: getEventColor(e) }}
                >
                  {e.title}
                </span>
              ))}

              {overflow > 0 && (
                <span className="px-1.5 text-[10px] text-text-muted font-medium leading-[14px]">
                  +{overflow}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
